import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CONTRACT_STATUSES, isContractStatus } from '@solar/contracts';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';
import styles from './AdminPortal.module.css';

export function AdminContractsPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(() => {
    const requestedStatus = searchParams.get('status');
    return requestedStatus !== null && isContractStatus(requestedStatus) ? requestedStatus : '';
  });
  const contracts = useQuery({
    queryKey: ['admin', 'contracts', { page, status }],
    queryFn: () => applicationsApi.listAdmin({ page, pageSize: 20, status: status || undefined }),
  });

  const items = contracts.data?.items.filter((item) => isContractStatus(item.status)) ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Contract operations</p>
          <h1 className={styles.pageTitle}>Contracts</h1>
          <p className={styles.pageSubtitle}>
            Track customer acceptance and active solar financing contracts.
          </p>
        </div>
      </header>

      <section className={styles.card} aria-labelledby="admin-contracts-heading">
        <h2 className={styles.cardTitle} id="admin-contracts-heading">
          All contracts
        </h2>

        <div className={styles.filters}>
          <label className={styles.filterLabel} htmlFor="contract-status-filter">
            Status
            <select
              id="contract-status-filter"
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
                setSearchParams(event.target.value === '' ? {} : { status: event.target.value });
              }}
            >
              <option value="">All contract statuses</option>
              {CONTRACT_STATUSES.map((option) => (
                <option key={option} value={option}>
                  {formatStatusLabel(option)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {contracts.isPending && <PageSpinner label="Loading contracts" />}
        {contracts.isError && (
          <Alert tone="error">
            {toDisplayMessage(contracts.error)}{' '}
            <Button variant="secondary" onClick={() => void contracts.refetch()}>
              Retry
            </Button>
          </Alert>
        )}

        {contracts.isSuccess && items.length === 0 && (
          <p className={styles.empty}>
            {status === ''
              ? 'No contracts yet. Approved applications will appear here after customer acceptance.'
              : 'No contracts match this status. Choose another status to widen the list.'}
          </p>
        )}

        {contracts.isSuccess && items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Product</th>
                  <th scope="col">Status</th>
                  <th scope="col">Financed</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <Link to={routeTo.adminApplication(contract.id)}>
                        {contract.contractReference ?? contract.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td>
                      <Link to={routeTo.adminCustomer(contract.customerId)}>
                        {contract.customerId.slice(0, 8).toUpperCase()}
                      </Link>
                    </td>
                    <td>
                      {contract.requestedTerms?.productName ??
                        contract.productNameSnapshot ??
                        'Legacy product'}
                    </td>
                    <td>
                      <StatusBadge status={contract.status} />
                    </td>
                    <td>{formatNgn(contract.financedAmount)}</td>
                    <td className={styles.rowAction}>
                      <Link to={routeTo.adminApplication(contract.id)}>View contract</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {contracts.isSuccess && contracts.data.items.length > 0 && (
          <nav className={styles.pagination} aria-label="Contract pages">
            <Button
              variant="secondary"
              disabled={!contracts.data.pagination.hasPreviousPage}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <span>
              Page {contracts.data.pagination.page} of {contracts.data.pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={!contracts.data.pagination.hasNextPage}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </nav>
        )}
      </section>
    </div>
  );
}
